import { Navigation } from '@/components/Navigation';
import { Footer } from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Clock, Calendar, User } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { SocialShare } from '@/components/SocialShare';
import { SEO } from '@/components/SEO';

export default function SlowParenting() {
  const navigate = useNavigate();
  
  const handleGoBack = () => {
    navigate('/blog');
  };

  return (
    <>
      <SEO 
        title="The Gentle Rebellion of Slow Parenting: Rediscovering Presence with Our Children"
        description="In a world that glorifies productivity, choosing simplicity in parenting is a quiet rebellion. Discover the science and wisdom behind slow parenting."
        keywords={["slow parenting", "mindful parenting", "presence", "child development", "family connection", "unstructured play"]}
        canonicalUrl="https://mysticforest.app/blog/slow-parenting"
        ogType="article"
      />
      <div className="min-h-screen bg-background">
        <Navigation />
        
        {/* Hero Section */}
        <section className="pt-32 pb-16 px-6">
          <div className="max-w-4xl mx-auto">
            <Button
              variant="ghost"
              onClick={handleGoBack}
              className="mb-8 -ml-4 text-muted-foreground hover:text-foreground"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Blog
            </Button>
            
            <div className="mb-8">
              <Badge className="mb-4">Parenting</Badge>
              <h1 className="text-4xl md:text-6xl font-serif font-light mb-6 text-foreground leading-tight">
                The Gentle Rebellion of Slow Parenting: Rediscovering Presence with Our Children
              </h1>
              
              <div className="flex items-center gap-6 text-sm text-muted-foreground mb-8">
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  <time dateTime="2025-10-11">October 11, 2025</time>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  <span>8 min read</span>
                </div>
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4" />
                  <span>Piotr</span>
                </div>
              </div>
              
              <SocialShare 
                url={window.location.href}
                title="The Gentle Rebellion of Slow Parenting: Rediscovering Presence with Our Children"
                description="In a world that glorifies productivity, choosing simplicity in parenting is a quiet rebellion."
              />
            </div>
            
            <div className="relative overflow-hidden rounded-2xl mb-12">
              <img 
                src="/lovable-uploads/slow-parenting-family.jpg"
                alt="Family jumping on bed together in natural light"
                className="w-full h-[400px] md:h-[500px] object-cover grayscale"
                loading="eager"
              />
            </div>
          </div>
        </section>

        {/* Article Content */}
        <article className="px-6 pb-24">
          <div className="max-w-4xl mx-auto">
            <div className="prose prose-lg max-w-none">
              <blockquote className="border-l-4 border-primary pl-6 my-8 italic text-xl text-muted-foreground">
                "Children have never been very good at listening to their elders, but they have never failed to imitate them."
                <footer className="text-sm text-muted-foreground mt-2 not-italic">
                  — James Baldwin, <em>Nobody Knows My Name</em>, New York: Dial Press, 1961, p. 125
                </footer>
              </blockquote>

              <div className="space-y-8 text-lg leading-relaxed text-foreground">
                <h2 className="text-3xl font-serif font-light mb-12 mt-12 text-foreground">The Race to Be a "Good" Parent</h2>
                
                <p>
                  Parenting today can feel like a competition disguised as care.
                  We curate childhoods with apps, schedules, and endless activities. Every weekend is a project: art class, language lesson, sports, mindfulness camp — all in the name of "development."
                </p>

                <p>
                  But what if this constant planning is not enriching our children's lives, but cluttering them?
                  What if the most important thing a child needs is not another experience, but a parent who has time to breathe?
                </p>

                <p>
                  In a world that glorifies productivity, many of us have internalized the belief that "doing more" equals "being better."
                  Yet children don't need a program — they need presence.
                </p>

                <h2 className="text-3xl font-serif font-light mb-12 mt-12 text-foreground">The Science of Slowness</h2>
                
                <p>
                  Research increasingly shows that unstructured play is essential for cognitive, emotional, and social development (Gray, 2011).
                  When children are left to explore, imagine, and problem-solve on their own, they develop executive function, creativity, and resilience — skills that can't be taught through worksheets or programs.
                </p>

                <p>
                  Yet according to a study from the University of Michigan (Hofferth & Sandberg, 2001), children's unstructured playtime has declined by 25% over the past two decades, replaced by organized sports and academic enrichment.
                </p>

                <p>
                  What's lost in this shift isn't just play — it's presence. When families rush from activity to activity, connection becomes transactional: "How was practice?" instead of "Tell me what you're thinking about."
                </p>

                <h2 className="text-3xl font-serif font-light mb-12 mt-12 text-foreground">The Myth of "Quality Time"</h2>
                
                <blockquote className="border-l-4 border-primary pl-6 my-8 italic text-xl text-muted-foreground">
                  "The best thing a parent can give a child is their attention — not their performance."
                  <footer className="text-sm text-muted-foreground mt-2 not-italic">
                    — Shefali Tsabary, <em>The Conscious Parent</em>, 2010
                  </footer>
                </blockquote>
                
                <p>
                  We've been sold the idea of "quality time" — brief, curated moments where we're fully engaged. But children don't need performance. They need availability.
                </p>

                <p>
                  Studies on attachment show that it's not the intensity of interaction that matters most, but consistency and emotional availability (Ainsworth et al., 1978). A child who knows their parent is present — not distracted, not anxious, just there — develops a secure sense of self.
                </p>

                <h2 className="text-3xl font-serif font-light mb-12 mt-12 text-foreground">Slowing Down as Resistance</h2>
                
                <p>
                  In his book <em>The Slow Professor</em>, Maggie Berg writes:
                </p>

                <blockquote className="border-l-4 border-primary pl-6 my-8 italic text-xl text-muted-foreground">
                  "Slowing down is not laziness — it is a deliberate choice to resist a culture that demands productivity above meaning."
                </blockquote>
                
                <p>
                  This same principle applies to parenting. Slow parenting is not permissive or passive — it's intentional. It's saying no to the cultural pressure to overschedule, overachieve, and over-document childhood.
                </p>

                <h2 className="text-3xl font-serif font-light mb-12 mt-12 text-foreground">The Benefits for Both Child and Parent</h2>
                
                <p>A slower rhythm benefits everyone:</p>
                <ul className="space-y-3 ml-6">
                  <li><strong className="text-accent">For children:</strong> More creativity, emotional regulation, and confidence in their own choices.</li>
                  <li><strong className="text-accent">For parents:</strong> Less stress, guilt, and burnout. More joy, connection, and alignment with values.</li>
                </ul>

                <p>
                  When families slow down, they rediscover what really matters — not how many activities a child completes, but how deeply they are known and seen.
                </p>

                <h2 className="text-3xl font-serif font-light mb-12 mt-12 text-foreground">Practices for a Slower Family Life</h2>
                
                <ul className="space-y-3 ml-6">
                  <li><strong className="text-accent">Limit activities to one per child per season.</strong></li>
                  <li><strong className="text-accent">Protect unstructured time.</strong> Let Saturdays be open. Let boredom lead to discovery.</li>
                  <li><strong className="text-accent">Create rituals without screens.</strong> Evening walks. Cooking together. Reading aloud.</li>
                  <li><strong className="text-accent">Model rest.</strong> If children see you collapse exhausted every night, they'll learn that rest is earned through suffering.</li>
                  <li><strong className="text-accent">Ask fewer questions.</strong> Instead of interrogating their day, sit with them in silence. Let them share when they're ready.</li>
                </ul>

                <h2 className="text-3xl font-serif font-light mb-12 mt-12 text-foreground">Mystic Forest Perspective</h2>
                
                <p>
                  At Mystic Forest, we believe slow parenting begins when parents themselves slow down. That's why we offer family retreats focused on presence, not programming — where children explore forests, play freely, and parents remember what it feels like to simply be.
                </p>

                <p className="text-xl font-light italic text-accent">
                  Childhood is not a race. It's a season meant to be savored.
                </p>

                <div className="bg-card p-8 rounded-2xl mt-16 border border-border/20">
                  <h3 className="text-2xl font-serif font-light mb-4 text-foreground">References</h3>
                  <ol className="space-y-2 text-sm text-muted-foreground list-decimal list-inside">
                    <li>Ainsworth, M. D. S., Blehar, M. C., Waters, E., & Wall, S. (1978). <em>Patterns of Attachment: A Psychological Study of the Strange Situation</em>. Hillsdale, NJ: Erlbaum.</li>
                    <li>Berg, Maggie, & Seeber, Barbara K. (2016). <em>The Slow Professor: Challenging the Culture of Speed in the Academy</em>. Toronto: University of Toronto Press.</li>
                    <li>Gray, Peter. (2011). "The Decline of Play and the Rise of Psychopathology in Children and Adolescents." <em>American Journal of Play</em>, 3(4), 443–463.</li>
                    <li>Hofferth, S. L., & Sandberg, J. F. (2001). "How American Children Spend Their Time." <em>Journal of Marriage and Family</em>, 63(2), 295–308.</li>
                    <li>Tsabary, Shefali. (2010). <em>The Conscious Parent: Transforming Ourselves, Empowering Our Children</em>. Vancouver: Namaste Publishing.</li>
                    <li>Baldwin, James. (1961). <em>Nobody Knows My Name</em>. New York: Dial Press.</li>
                  </ol>
                </div>
              </div>
            </div>
          </div>
        </article>

        <Footer />
      </div>
    </>
  );
}
